import { useState } from 'react'

import { useParams } from 'react-router'

import { useGetCatalogAccessSettingsPresenter } from '@webapp/modules/purchasing/features/get-catalog-access-settings'
import { useLockAllCatalogItemsPresenter } from '@webapp/modules/purchasing/features/lock-all-catalog-items'
import { useToggleCatalogLockDefaultPresenter } from '@webapp/modules/purchasing/features/toggle-catalog-lock-default'
import { useToggleCatalogPermissionPresenter } from '@webapp/modules/purchasing/features/toggle-catalog-permission'
import { useUnlockAllCatalogItemsPresenter } from '@webapp/modules/purchasing/features/unlock-all-catalog-items'

type ActiveTab = 'users' | 'departments'

/**
 * Local toggle state that re-syncs from the server value during render (not
 * in an effect), so the first settled render already shows server truth and
 * a server change to one setting can never clobber optimistic local state
 * of the other.
 */
const useServerSyncedToggle = (serverValue: boolean | undefined) => {
  const [value, setValue] = useState(serverValue ?? false)
  const [syncedValue, setSyncedValue] = useState(serverValue)

  if (serverValue !== undefined && serverValue !== syncedValue) {
    setSyncedValue(serverValue)
    setValue(serverValue)
  }

  return [value, setValue] as const
}

export const useCatalogAccessSettingsView = () => {
  const { tab } = useParams<{ tab: string }>()
  const activeTab: ActiveTab = tab === 'departments' ? 'departments' : 'users'

  const {
    data: { settings },
    states: { isLoading, isError },
  } = useGetCatalogAccessSettingsPresenter()

  const [rfoLockDefault, setRfoLockDefault] = useServerSyncedToggle(
    settings?.rfoLockDefault
  )
  const [catalogPermissionEnabled, setCatalogPermissionEnabled] =
    useServerSyncedToggle(settings?.catalogPermissionEnabled)

  const catalockToggle = useToggleCatalogLockDefaultPresenter({
    checked: rfoLockDefault,
    onChange: setRfoLockDefault,
  })

  const permissionToggle = useToggleCatalogPermissionPresenter({
    onChange: setCatalogPermissionEnabled,
  })

  const [isLockAllOpen, setIsLockAllOpen] = useState(false)
  const [isUnlockAllOpen, setIsUnlockAllOpen] = useState(false)

  const openLockAll = () => setIsLockAllOpen(true)
  const openUnlockAll = () => setIsUnlockAllOpen(true)
  const dismissLockAll = () => setIsLockAllOpen(false)
  const dismissUnlockAll = () => setIsUnlockAllOpen(false)

  const lockAllPresenter = useLockAllCatalogItemsPresenter()
  const unlockAllPresenter = useUnlockAllCatalogItemsPresenter()

  // The Feature Set owns the modal outcome: close on success, stay open for
  // a retry on failure (the presenter has already toasted the error). The
  // boolean tells the view whether the modal is still open.
  const confirmLockAll = async (): Promise<boolean> => {
    try {
      await lockAllPresenter.commands.lockAll()
      dismissLockAll()
      return true
    } catch {
      return false
    }
  }

  const confirmUnlockAll = async (): Promise<boolean> => {
    try {
      await unlockAllPresenter.commands.unlockAll()
      dismissUnlockAll()
      return true
    } catch {
      return false
    }
  }

  return {
    states: {
      isLoading,
      isError,
      activeTab,
      rfoLockDefault,
      catalogPermissionEnabled,
      isCatalockSaving: catalockToggle.states.isSaving,
      isPermissionSaving: permissionToggle.states.isSaving,
      isLockAllOpen,
      isUnlockAllOpen,
    },
    commands: {
      toggleCatalock: catalockToggle.commands.toggle,
      togglePermission: permissionToggle.commands.toggle,
      openLockAll,
      confirmLockAll,
      dismissLockAll,
      openUnlockAll,
      confirmUnlockAll,
      dismissUnlockAll,
    },
  }
}
